import React, { useEffect, useState, useCallback, lazy, Suspense } from "react";
import NeedCategories from "./NeedCategories";
import { Row, Form, Col, FormGroup, Label, Button, Input } from "reactstrap";
import { useSelector, useDispatch } from "react-redux";
import AsyncSelect from "react-select/async";
import {
  fetchGurdwaras,
  listGurudwaras,
} from "../../../components/Header/Data";
import {
  setTitle,
  setGurudwara,
  setDescription,
  setZipcode,
  setPhone,
  setDialExtension,
  setTerms,
  setAlternativePhoneOne,
  setAlternativePhoneTwo,
  setTimeZone,
  setNeedRadius,
  setCityName,
  setCheckedValue,
  setWebsiteLink,
} from "../../../redux/slices/RequestFormSlice";
import debounce from "lodash/debounce";

const NeedDescription = lazy(() => import("./NeedDescription"));
const WhomToNotify = lazy(() => import("./WhomToNotify"));
const NeedPhotos = lazy(() => import("./NeedPhotos"));

const CreateNeed = ({
  categories,
  subCategories1,
  subCategories,
  setSubCategories,
  editMode,
  handleFormSubmit,
  selectedCat,
  setSelectedCat,
  isLoading,
}) => {
  const dispatch = useDispatch();
  const islogin = useSelector((state) => state.user.isLogin);
  const userinfo = useSelector((state) => state.user.userInfo);
  const user = islogin ? JSON.parse(userinfo) : null;
  const [gurdwaras, setGurdwaras] = useState([]);
  const formData = useSelector((state) => state.requestForm);
  const isAdmin = user && user.Role === "admin";
  const [usCities, setUsCities] = useState([]);
  const [otherCities, setOtherCities] = useState([]);

  const fetchData = async () => {
    const gurdwaras = await listGurudwaras();
    setGurdwaras(gurdwaras);
  };

  useEffect(() => {
    const fetchCities = async () => {
      try {
        const response = await fetch(
          "https://countriesnow.space/api/v0.1/countries",
          {
            method: "GET",
            headers: { "Content-Type": "application/json" },
          }
        );
        const data = await response.json();
        if (data.error) throw new Error(data.msg);

        const usCitiesData =
          data.data.find((country) => country.country === "United States")
            ?.cities || [];
        const otherCitiesData = data.data
          .filter((country) => country.country !== "United States")
          .flatMap((country) => country.cities);

        setUsCities(usCitiesData);
        setOtherCities(otherCitiesData);
      } catch (error) {
        console.error("Error fetching cities:", error);
        setUsCities([]);
        setOtherCities([]);
      }
    };
    fetchCities();
    fetchData();
  }, []);

  const loadOptions = useCallback(
    debounce((inputValue, callback) => {
      const inputLower = inputValue.toLowerCase();
      const filteredUsCities = usCities
        .filter((city) => city.toLowerCase().includes(inputLower))
        .slice(0, 50)
        .map((city) => ({ label: city, value: city }));
      const remainingSlots = 50 - filteredUsCities.length;
      const filteredOtherCities =
        remainingSlots > 0
          ? otherCities
              .filter((city) => city.toLowerCase().includes(inputLower))
              .slice(0, remainingSlots)
              .map((city) => ({ label: city, value: city }))
          : [];
      callback([...filteredUsCities, ...filteredOtherCities]);
    }, 300),
    [usCities, otherCities]
  );

  const CityNames = () => {
    return (
      <FormGroup>
        <Label>
          City Name <b className="text-danger">*</b>
        </Label>
        <AsyncSelect
          cacheOptions
          defaultOptions={usCities
            .slice(0, 50)
            .map((city) => ({ label: city, value: city }))}
          loadOptions={loadOptions}
          value={
            formData?.cityName
              ? { label: formData.cityName, value: formData.cityName }
              : null
          }
          onChange={(selectedOption) => {
            const selectedCity = selectedOption ? selectedOption.value : "";
            dispatch(setCityName(selectedCity));
          }}
          placeholder="Type Your City Name..."
          noOptionsMessage={() => "No cities found"}
          isClearable
          openMenuOnFocus={false}
          openMenuOnClick={false}
        />
      </FormGroup>
    );
  };

  return (
    <Form className="wc_create-request" onSubmit={handleFormSubmit}>
      <Col className="request-form p-2 p-lg-4 mt-4">
        <p className="text-center">Need/Request</p>
        <NeedCategories
          categories={categories}
          subCategories1={subCategories1}
          subCategories={subCategories}
          setSubCategories={setSubCategories}
          selectedCat={selectedCat}
          setSelectedCat={setSelectedCat}
        />
        <Row>
          <Col xs="12" sm="6">
            <FormGroup>
              <Label>
                Enter Ad Title <b className="text-danger">*</b>
              </Label>
              <Input
                type="text"
                name="Title"
                placeholder="Ex: Seeking an event Decorator great with Flowers"
                value={formData.Title}
                onChange={(e) => dispatch(setTitle(e.target.value))}
              />
            </FormGroup>
          </Col>
          <Col xs="12" sm="6">
            <FormGroup>
              <Label>
                Select Gurdwara Near You <b className="text-danger">*</b>
              </Label>
              <Input
                type="select"
                name="Gurudwara"
                onChange={(e) => dispatch(setGurudwara(e.target.value))}
                value={formData.Gurudwara}
              >
                <option value="">Select Gurdwara</option>
                {gurdwaras.map((gurdwara) => (
                  <option key={gurdwara.Id} value={gurdwara.Title}>
                    {gurdwara.Title}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Col>
        </Row>
        <Suspense fallback={<div>Loading description...</div>}>
          <NeedDescription />
        </Suspense>
        <Suspense fallback={<div>Loading city options...</div>}>
          {CityNames()}
        </Suspense>
        <Row>
          <Col sm={12} lg={isAdmin ? 4 : 6}>
            <FormGroup>
              <Label>
                Enter Zip Code <b className="text-danger">*</b>
              </Label>
              <Input
                type="text"
                name="Zipcode"
                placeholder="Enter Zip code"
                onChange={(e) => {
                  const { value } = e.target;
                  const isValidZipCode = /^\d{0,5}$/.test(value);
                  if (isValidZipCode) {
                    dispatch(setZipcode(value));
                  }
                }}
                value={formData.Zipcode}
              />
            </FormGroup>
          </Col>
          <Col sm={12} lg={isAdmin ? 4 : 6}>
            <FormGroup>
              <Label>
                Timezone <b className="text-danger">*</b>
              </Label>
              <Input
                type="select"
                name="TimeZone"
                value={formData?.timeZone}
                onChange={(e) => dispatch(setTimeZone(e.target.value))}
              >
                <option value="America/New_York, Eastern Time">
                  America/New_York (Eastern Time)
                </option>
                <option value="America/Chicago, Central Time">
                  America/Chicago (Central Time)
                </option>
                <option value="America/Denver, Mountain Time">
                  America/Denver (Mountain Time)
                </option>
                <option value="America/Los_Angeles, Pacific Time">
                  America/Los_Angeles (Pacific Time)
                </option>
                <option value="UTC, Coordinated Universal Time">
                  UTC (Coordinated Universal Time)
                </option>
                <option value="Europe/Berlin, Central European Time">
                  Europe/Berlin (Central European Time)
                </option>
              </Input>
            </FormGroup>
          </Col>
          {isAdmin && (
            <Col sm={12} lg={4}>
              <FormGroup>
                <Label>
                  Radius <b className="text-danger">*</b>
                </Label>
                <Input
                  type="select"
                  name="radius"
                  value={formData.needRadius}
                  onChange={(e) => dispatch(setNeedRadius(e.target.value))}
                >
                  <option value="">Select radius</option>
                  <option value="10">10 miles</option>
                  <option value="20">20 miles</option>
                  <option value="30">30 miles</option>
                  <option value="50">50 miles</option>
                  <option value="100">100 miles</option>
                </Input>
              </FormGroup>
            </Col>
          )}
        </Row>
        {isAdmin && (
          <Row>
            <Label>Select to Add Mobile Number or Website Link</Label>
            <FormGroup>
              <Label check className="me-1">
                <Input
                  type="checkbox"
                  name="isMobile"
                  value="mobile"
                  checked={formData?.checkedValue === "mobile"}
                  onChange={(e) => {
                    dispatch(setCheckedValue(e.target.value));
                    dispatch(setWebsiteLink(""));
                  }}
                />{" "}
                Mobile Number
              </Label>
              <Label check className="ms-1">
                <Input
                  type="checkbox"
                  name="isWebsite"
                  value="website"
                  checked={formData?.checkedValue === "website"}
                  onChange={(e) => {
                    dispatch(setCheckedValue(e.target.value));
                    dispatch(setPhone(""));
                    dispatch(setAlternativePhoneOne(""));
                    dispatch(setAlternativePhoneTwo(""));
                    dispatch(setDialExtension(""));
                  }}
                />{" "}
                Website Link
              </Label>
            </FormGroup>
          </Row>
        )}
        {(formData?.checkedValue === "mobile" || !isAdmin) && (
          <Row>
            <Col sm={12} lg={isAdmin ? 3 : 4} className="phone">
              <FormGroup>
                <Label>
                  Mobile Number <b className="text-danger">*</b>
                </Label>
                <Input
                  type="number"
                  name="Phone"
                  value={formData.Phone}
                  onChange={(e) => {
                    const { value } = e.target;
                    const isValidPhone = /^\d{0,10}$/.test(value);
                    if (isValidPhone) {
                      dispatch(setPhone(value));
                    }
                  }}
                />
              </FormGroup>
            </Col>
            <Col sm={12} lg={isAdmin ? 3 : 4} className="alterPhone-1">
              <FormGroup>
                <Label>
                  Mobile Number (family member) <b className="text-danger">*</b>
                </Label>
                <Input
                  type="number"
                  name="alternativePhoneOne"
                  placeholder="This improves call answers"
                  value={formData.alternativePhoneOne}
                  onChange={(e) => {
                    const { value } = e.target;
                    const isValidPhone = /^\d{0,10}$/.test(value);
                    if (isValidPhone) {
                      dispatch(setAlternativePhoneOne(value));
                    }
                  }}
                />
              </FormGroup>
            </Col>
            <Col sm={12} lg={isAdmin ? 3 : 4} className="alterPhone-2">
              <FormGroup>
                <Label>Mobile Number (family member)</Label>
                <Input
                  type="number"
                  name="alternativePhoneTwo"
                  placeholder="This improves call answers"
                  value={formData.alternativePhoneTwo}
                  onChange={(e) => {
                    const { value } = e.target;
                    const isValidPhone = /^\d{0,10}$/.test(value);
                    if (isValidPhone) {
                      dispatch(setAlternativePhoneTwo(value));
                    }
                  }}
                />
              </FormGroup>
            </Col>
            {isAdmin && (
              <Col sm={12} lg={3} className="diel-ext">
                <FormGroup>
                  <Label>
                    Dial Extension <b className="text-danger">*</b>
                  </Label>
                  <Input
                    placeholder="Dial Extension"
                    type="text"
                    name="dialExtension"
                    value={formData.dialExtension}
                    onChange={(e) => {
                      const { value } = e.target;
                      const isValid = /^\d{0,5}$/.test(value);
                      if (isValid) {
                        dispatch(setDialExtension(value));
                      }
                    }}
                    disabled={user?.RoleId === 1 ? false : true}
                  />
                </FormGroup>
              </Col>
            )}
          </Row>
        )}
        {formData?.checkedValue === "website" && (
          <FormGroup>
            <Label>
              Website URL <b className="text-danger">*</b>
            </Label>
            <Input
              type="text"
              name="websiteUrl"
              placeholder="Enter website URL"
              value={formData.websiteLink}
              onChange={(e) => dispatch(setWebsiteLink(e.target.value))}
            />
          </FormGroup>
        )}
        <Suspense fallback={<div>Loading photos...</div>}>
          <NeedPhotos />
        </Suspense>
      </Col>
      {isAdmin && (
        <Col className="request-form p-4 mt-3">
          <h5 className="pb-3">Whom To Be Notified</h5>
          <Suspense fallback={<div>Loading notification settings...</div>}>
            <WhomToNotify />
          </Suspense>
        </Col>
      )}
      <Col className="request-form p-4 mt-5">
        <h5 className="pb-3">
          Terms <b className="text-danger">*</b>
        </h5>
        <h6 className="fst-italic pb-3 small">
          <p className="text-justify pb-1">
            1. Please note that MySikhCommunity.ORG & Gurdwara Sahib assume no
            responsibility for the results of your interactions with
            advertisers.
          </p>
          <p className="text-justify pb-2 px-2">
            Additionally, by choosing to make your AD public on the community
            board, you agree to freely disclose your AD data to organizations or
            individuals.
          </p>
          <p className="text-justify">
            2. If you wish to have the AD posted in English & Punjabi, then
            please call us. We will type & print the AD for you in both
            languages. (800)245-3610.
          </p>
        </h6>
        <div className="mb-3 form-check">
          <FormGroup>
            <Input
              type="checkbox"
              name="Terms"
              checked={formData.Terms === 1}
              onChange={(e) => {
                dispatch(setTerms(e.target.checked ? 1 : 0));
              }}
              className="form-check-input"
              id="exampleCheck1"
            />
            <Label
              className="form-check-label ps-2"
              htmlFor="exampleCheck1"
              required
            >
              I Understand And Accept Terms
            </Label>
          </FormGroup>
        </div>
      </Col>
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "flex-end",
          padding: "10px 2px 10px 10px",
        }}
      >
        <Button
          type="submit"
          className="btn btn-danger mb-2"
          disabled={isLoading}
        >
          {Object.keys(editMode).length > 0
            ? "Update Request"
            : "Submit Request"}
        </Button>
      </div>
    </Form>
  );
};

export default CreateNeed;
